import { useEffect, useRef, useState } from "react";
import { useApp } from "@/store/AppStore";

const MAX_BYTES = 400 * 1024; // 400 KB

// Panel de identidad de marca: permite subir el logo oficial manualmente.
// Mientras no se suba ninguno, la app usa el monograma ATT por defecto.
const BrandIdentityPanel = () => {
  const { data, updateSettings } = useApp();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const logo = data.settings.logoUrl;

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onFile = (file?: File) => {
    setError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Selecciona un archivo de imagen (PNG, JPG o SVG).");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("La imagen supera 400 KB. Usa una versión más liviana.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      void updateSettings({ logoUrl: String(reader.result) });
      setOpen(false);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    void updateSettings({ logoUrl: "" });
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 flex items-center justify-center rounded-md border border-accent-500/20 bg-background-200/50 text-foreground-700 hover:text-foreground-950 hover:border-accent-500/40 cursor-pointer"
        aria-label="Identidad de marca"
        title="Logo de marca"
      >
        <i className="ri-image-2-line text-lg" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[280px] glass-panel-strong rounded-lg p-4 animate-slide-down z-50">
          <p className="text-sm font-semibold text-foreground-950">Identidad de marca</p>
          <p className="text-[11px] text-foreground-500 mb-3">
            Sube el logo oficial de Amway Tienda Digital Domicilios. Si no subes nada, se muestra el
            monograma ATT.
          </p>

          <div className="flex items-center justify-center rounded-lg border border-background-300/50 bg-background-100/60 h-24 mb-3 overflow-hidden">
            {logo ? (
              <img
                src={logo}
                alt="Logo actual"
                className="max-h-20 max-w-[200px] object-contain"
              />
            ) : (
              <span className="text-xs text-foreground-500">Sin logo (monograma ATT)</span>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />

          {error && (
            <p className="text-[11px] text-primary-200 flex items-center gap-1.5 mb-2">
              <i className="ri-error-warning-line" /> {error}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <button
              onClick={() => inputRef.current?.click()}
              className="px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-upload-2-line" /> Subir logo
            </button>
            {logo && (
              <button
                onClick={removeLogo}
                className="px-3 py-2 rounded-md border border-background-300/50 text-foreground-600 hover:text-foreground-900 text-xs font-semibold whitespace-nowrap cursor-pointer"
              >
                Quitar logo
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandIdentityPanel;